import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { archivedPdfUrl, getArchivedPdf, getStatementPreviewPdf } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { Button } from '@/components/Button';
import { ErrorState, Skeleton } from '@/components/States';
import { money, num, dateTime } from '@/lib/format';
import { monthRange, useGet, useStlPerms, useUrlState, type StatementAction } from './api';
import { EPS, LABELS, STATEMENT_FLOW, basisLabel, statementNext, statementStatus } from './status';
import type { CashRecord, StatementListItem, StatementPayload, StatementStatus } from './types';
import { termsText } from './PartnerList';
import { DocList } from './DocList';
import { RecordList, toVM } from './RecordList';
import { StatementDoc } from './StatementDoc';
import { SupplierStatementWorkspace } from './SupplierStatementWorkspace';
import { StatementActionDialog } from './StatementActionDialog';
import { StatementPdfDialog } from './StatementPdfDialog';
import { StatementRefreshDialog } from './StatementRefreshDialog';
import { Kpis, Section, Seg, bigMoney, type Kpi } from './ui';
import s from './StatementDetail.module.css';

type V2StatusEvent = { id: string; action: string; createdAt: string; by: string; from: string; to: string; reason: string };
const eventName: Record<string, string> = { confirm: '确认', reopen: '反确认', lock: '锁账', unlock: '解锁', cancel: '作废' };

/**
 * 四态状态机画成一张卡：三步主线 + 旁路作废（高保真稿 T8）。流转照原系统：
 * 确认（草稿→已确认）、锁账（已确认→已锁账）、反确认（已确认 / 已锁账 → 草稿）。
 */
function Flow({ status }: { status: StatementStatus }) {
  const idx = STATEMENT_FLOW.indexOf(status);
  const cancelled = status === 'CANCELLED';
  const next = statementNext(status);
  const arrows = ['确认', '锁账'];
  return (
    <div className={s.flow}>
      <ol className={cancelled ? s.off : undefined}>
        {STATEMENT_FLOW.map((k, i) => (
          <li key={k} className={i < idx ? s.done : i === idx ? s.now : undefined}>
            {i > 0 && <span className={s.arrow}><small>{arrows[i - 1]}</small></span>}
            <i>{i < idx ? <Icon name="check" size={14} /> : i + 1}</i>
            <b>{statementStatus[k].label}</b>
          </li>
        ))}
      </ol>
      <div className={`${s.flowNotes} ${cancelled ? s.sideOn : ''}`}>
        <p className={s.backLine}><Icon name="return" size={14} /><span>已确认可反确认回草稿；已锁账先解锁回已确认。已作废是旁路状态，不删除底层单据和台账。</span></p>
        <p className={s.flowNext}><b>{next.title}</b><span>{next.text}</span></p>
      </div>
    </div>
  );
}

export function StatementDetail({ id, embedded }: { id: string; embedded?: boolean }) {
  const ff = useFormFactor();
  const nav = useNavigate();
  const perms = useStlPerms();
  const [sp, set] = useUrlState();
  const [action, setAction] = useState<StatementAction | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const view = sp.get('v') === 'doc' ? 'doc' : 'sum';
  const res = useGet<StatementPayload>(`/api/settlements/statements/${id}`);
  const history = useGet<V2StatusEvent[]>(`/api/v2/settlements/statements/${id}/history`);
  const d = res.fresh ? res.data : undefined;
  const scope = d?.scope;
  const partnerId = d?.partner.id;
  const idKey = scope === 'supplier' ? 'supplierId' : 'customerId';
  const others = useGet<StatementListItem[]>(scope && partnerId ? '/api/settlements/statements' : null, { list: 1, scope, partnerId });
  // 老接口的快照只固化了业务单据，收付款要另查台账（实时数据，不属于快照）
  const cash = useGet<CashRecord[]>(scope && partnerId && perms.scope(scope) ? '/api/settlements/cash-records' : null, {
    partnerType: scope === 'supplier' ? 'SUPPLIER' : 'CUSTOMER',
    [idKey]: partnerId,
    month: d?.period,
  });

  // 加载中 / 失败也要留返回键：手机深层页没有 Tab 栏
  const bare = (body: ReactNode) => (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${ff === 'phone' && !embedded ? s.phone : ''}`}>
      {!embedded && (
        <header className={s.head}>
          <button type="button" className={s.back} onClick={() => (window.history.length > 1 ? nav(-1) : nav('/settlements/statements'))} aria-label="返回"><Icon name="back" size={20} /></button>
          <h1>对账单</h1>
        </header>
      )}
      <div className={s.scroll}>{body}</div>
    </div>
  );
  if (res.error) return bare(<ErrorState title="对账单加载失败" message={res.error.message} detail={`GET /api/settlements/statements/${id}`} onRetry={res.reload} />);
  if (!d || !scope) return bare(<><Skeleton /><Skeleton lines={6} /></>);

  const L = LABELS[scope];
  const status = d.status || 'DRAFT';
  const st = statementStatus[status];
  const sum = d.summary;
  const receiptPeriodBasis = scope === 'supplier' && d.businessDocuments.length > 0 && d.businessDocuments.every((doc) => doc.goods?.length && doc.goods.every((good) => good.billingBasis === 'RECEIVED' && good.receiptPeriod === d.period));
  const displayedBasis = receiptPeriodBasis ? '实际入库日期' : basisLabel(d.dateBasis, scope);
  const phone = ff === 'phone' && !embedded;
  const supplierWorkspace = view === 'doc' && scope === 'supplier' && ff === 'desktop' && !embedded;
  const [y, m] = d.period.split('-');
  const actionItem: StatementListItem = {
    id, statementNo: d.statementNo || id, partnerType: scope === 'supplier' ? 'SUPPLIER' : 'CUSTOMER',
    customerId: scope === 'customer' ? d.partner.id : undefined, supplierId: scope === 'supplier' ? d.partner.id : undefined,
    partner: d.partner, settlementMonth: d.period, dateBasis: d.dateBasis, status,
    billAmount: sum.billAmount, cashAmount: sum.cashAmount, cashBalance: sum.cashBalanceAmount,
    pdfFileUrl: d.pdfFileUrl, lockedAt: d.lockedAt, lockedBy: d.lockedBy,
    createdAt: d.createdAt || '', updatedAt: d.updatedAt || '', lineCount: d.businessDocuments.length,
  };
  const actionDialog = action && <StatementActionDialog item={actionItem} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); res.reload(); others.reload(); history.reload(); }} />;
  const mainAction: StatementAction | null = status === 'DRAFT' && perms.confirm ? 'confirm'
    : status === 'CONFIRMED' && perms.lock ? 'lock' : status === 'LOCKED' && perms.lock ? 'unlock' : null;
  const secondaryAction: StatementAction | null = status === 'CONFIRMED' && perms.confirm ? 'reopen' : null;
  const label = (value: StatementAction) => ({ confirm: '确认对账单', reopen: '反确认', lock: '锁账', unlock: '解锁', cancel: '作废对账单' })[value];

  const pdf = <Button icon="file" className={s.pdf} disabled={status !== 'LOCKED'} disabledReason="只有已锁账的对账单才能生成正式 PDF" onClick={() => setPdfOpen(true)}>生成 PDF</Button>;
  const seg = <Seg small={!phone} options={[{ value: 'sum', label: '汇总版' }, { value: 'doc', label: '明细版' }]} value={view} onChange={(v) => set({ v: v === 'doc' ? 'doc' : undefined })} />;
  const shareDocument = async () => {
    if (sharing || status === 'CANCELLED') return;
    setSharing(true);
    try {
      const preview = status === 'DRAFT' || status === 'CONFIRMED';
      const fileName = preview ? `对账单-${d.statementNo}-明细版-预览.pdf` : d.detailPdfFileName || `对账单-${d.statementNo}-明细版.pdf`;
      let bytes: Uint8Array<ArrayBuffer>;
      if (preview) bytes = await getStatementPreviewPdf(id, 'detail');
      else {
        const url = archivedPdfUrl(d.detailPdfFileUrl);
        if (!url) { setPdfOpen(true); return; }
        bytes = await getArchivedPdf(url);
      }
      const file = new File([bytes], fileName, { type: 'application/pdf' });
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ files: [file], title: fileName });
      } else {
        const url = URL.createObjectURL(file);
        const anchor = document.createElement('a');
        anchor.href = url; anchor.download = fileName; document.body.append(anchor); anchor.click(); anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (error) {
      if (!(error instanceof Error) || error.name !== 'AbortError') setPdfOpen(true);
    } finally { setSharing(false); }
  };

  const kpis: Kpi[] = sum.billAmount === undefined
    ? [{ label: '单据', value: `${d.businessDocuments.length} 张` }]
    : [
        { label: scope === 'supplier' ? '采购单数' : '单据数', value: `${d.businessDocuments.length}` },
        { label: scope === 'supplier' ? '采购金额' : '业务金额', value: bigMoney(sum.billAmount) },
        { label: scope === 'supplier' ? '已付' : '已收', value: bigMoney(sum.cashAmount), tone: 'blue' },
        { label: scope === 'supplier' ? '未付' : '未收', value: bigMoney(Math.abs(num(sum.cashBalanceAmount))), tone: num(sum.cashBalanceAmount) > EPS ? 'bad' : num(sum.cashBalanceAmount) < -EPS ? 'over' : 'mute' },
        { label: scope === 'supplier' ? '未核票' : '未开票', value: bigMoney(Math.abs(num(sum.invoiceBalanceAmount))), tone: num(sum.invoiceBalanceAmount) > EPS ? 'warn' : 'mute' },
      ];
  const phoneKpis = kpis.length === 5 ? kpis.slice(1) : kpis;

  const timeline: { verb: string; who?: string; at?: string | null; note?: string }[] = [
    { verb: '生成对账单', who: d.createdBy?.name, at: d.createdAt, note: `纳入 ${d.businessDocuments.length} 张单据 · 日期口径「${displayedBasis}」${sum.billAmount !== undefined ? ` · 金额 ${money(sum.billAmount)}` : ''}` },
    ...(d.confirmedAt ? [{ verb: '确认', who: d.confirmedBy?.name, at: d.confirmedAt }] : []),
    ...(d.lockedAt ? [{ verb: '锁账', who: d.lockedBy?.name, at: d.lockedAt }] : []),
    ...(d.pdfGeneratedAt ? [{ verb: '生成 PDF', at: d.pdfGeneratedAt, note: d.pdfFileName || undefined }] : []),
    ...(status === 'CANCELLED' ? [{ verb: '作废', at: d.updatedAt, note: '时间取最后更新时间' }] : []),
  ];
  const siblings = (others.data ?? []).filter((x) => x.id !== d.id);
  const splitTo = (sid: string) => {
    const n = new URLSearchParams(sp);
    n.set('id', sid);
    n.delete('v');
    return `/settlements/statements?${n.toString()}`;
  };

  const info = (
    <>
      <Section title="基本信息">
        <dl className={s.kv}>
          <div><dt>对账单号</dt><dd className="tnum">{d.statementNo}</dd></div>
          <div><dt>往来单位</dt><dd>{d.partner.name}</dd></div>
          <div><dt>类型</dt><dd>{scope === 'supplier' ? '供应商应付' : '客户应收'}</dd></div>
          <div><dt>结算月份</dt><dd className="tnum">{d.period}</dd></div>
          <div><dt>日期口径</dt><dd>{displayedBasis}</dd></div>
          <div><dt>结算方式</dt><dd>{termsText(d.partner)}</dd></div>
          <div><dt>生成人</dt><dd>{d.createdBy?.name || '—'} · <span className="tnum">{dateTime(d.createdAt)}</span></dd></div>
          {d.note && <div><dt>备注</dt><dd>{d.note}</dd></div>}
        </dl>
      </Section>
      <Section title="状态记录">
        <ol className={s.tl}>
          {timeline.map((t, i) => (
            <li key={i}>
              <div><b>{t.who ? `${t.who} ` : ''}{t.verb}</b><span className="tnum">{dateTime(t.at)}</span></div>
              {t.note && <p>{t.note}</p>}
            </li>
          ))}
        </ol>
        <p className={s.tlNote}>共享对账单保留最近一次确认和锁账；V2 的状态操作及原因记录在下方。</p>
      </Section>
      {!!history.data?.length && <Section title="V2 状态变更记录">
        <ol className={s.tl}>{history.data.map((event) => <li key={event.id}>
          <div><b>{event.by} {eventName[event.action] || event.action}</b><span className="tnum">{dateTime(event.createdAt)}</span></div>
          <p>{statementStatus[event.from as StatementStatus]?.label || event.from} → {statementStatus[event.to as StatementStatus]?.label || event.to}{event.reason ? ` · 原因：${event.reason}` : ''}</p>
        </li>)}</ol>
      </Section>}
      {siblings.length > 0 && (
        <Section flush title={<>同单位的其他对账单<small>{siblings.length} 份</small></>}>
          <ul className={s.hist}>
            {siblings.map((x) => (
              <li key={x.id}>
                <Link to={embedded ? splitTo(x.id) : `/settlements/statements/${x.id}`} replace={!embedded}>
                  <div><b className="tnum">{x.settlementMonth}</b> <Tag tone={statementStatus[x.status].tone} sm>{statementStatus[x.status].label}</Tag><small className="tnum">{x.statementNo}{x.billAmount !== undefined ? ` · ${money(x.billAmount)}` : ''}</small></div>
                  <Icon name="chev" className={s.chev} />
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {status !== 'CANCELLED' && perms.statementWrite && <button type="button" className={s.cancelAction} onClick={() => setAction('cancel')}>作废对账单</button>}
    </>
  );

  const cashRows = (cash.data ?? []).map((r) => toVM(r));
  const snapshot = <Section flush title={<>快照明细<small>{d.businessDocuments.length} 张 · 保存时固化，业务单据之后再改，这里不变</small></>}>
    <DocList docs={d.businessDocuments} scope={scope} empty="这份对账单没有纳入业务单据。" mode={phone ? 'cards' : 'compact-narrow'} />
  </Section>;
  const periodRecords = perms.scope(scope) && <Section flush title={<>期间{L.cashVerb}<small>台账里的实时记录，不属于快照</small></>}>
    {cash.error ? <ErrorState title={`${L.cashRecord}加载失败`} message={cash.error.message} onRetry={cash.reload} />
      : !cash.data ? <div className={s.pad}><Skeleton lines={2} /></div>
      : <RecordList kind="cash" scope={scope} rows={cashRows} showPartner={false} empty={`${d.period} 没有${L.cashRecord}。`} />}
  </Section>;
  const main = (
    <>
      <Flow status={status} />
      {embedded && status === 'DRAFT' && perms.statementWrite && <div className={s.statusActions}><Button icon="ref" onClick={() => setRefreshOpen(true)}>重新取数</Button></div>}
      {embedded && (mainAction || secondaryAction) && <div className={s.statusActions}>{secondaryAction && <button type="button" onClick={() => setAction(secondaryAction)}>{label(secondaryAction)}</button>}{mainAction && <button type="button" className={s.primaryAction} onClick={() => setAction(mainAction)}>{label(mainAction)}</button>}</div>}
      <Kpis items={kpis} className={s.kpis} />
      {snapshot}
      {periodRecords}
    </>
  );
  const phoneFormats = <section className={s.formats} aria-label="版式与导出">
    <h2>版式与导出</h2>
    <button type="button" onClick={() => set({ v: 'doc' })}>
      <Icon name="list" size={20} /><span><b>明细版 · 给客户看</b><small>按订单列出每一项货物 · {d.businessDocuments.length} 张订单</small></span><Icon name="chev" size={16} />
    </button>
    <button type="button" onClick={() => setPdfOpen(true)}>
      <Icon name="file" size={20} /><span><b>导出 PDF</b><small>{status === 'LOCKED' ? '已锁账，可导出正式 PDF' : status === 'CANCELLED' ? '已作废，不能导出 PDF' : `${st.label}只能导出预览版（带水印）`}</small></span><Icon name="chev" size={16} />
    </button>
  </section>;
  const phoneMain = (
    <>
      <div className={s.phoneIdentity}>
        <div><b className="tnum">{d.statementNo}</b><Tag tone={st.tone} sm>{st.label}</Tag></div>
        <p>{d.partner.name} · {Number(y)} 年 {Number(m)} 月 · {displayedBasis}口径</p>
        <small>{d.createdBy?.name || '—'} · {dateTime(d.createdAt)} 生成</small>
      </div>
      <Kpis items={phoneKpis} className={s.kpis} />
      <Flow status={status} />
      {phoneFormats}
      {snapshot}
      {periodRecords}
      {info}
    </>
  );

  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${phone ? s.phone : ''}`}>
      <header className={s.head}>
        {!embedded && (
          <button type="button" className={s.back} onClick={() => (window.history.length > 1 ? nav(-1) : nav('/settlements/statements'))} aria-label="返回">
            <Icon name="back" size={20} />
          </button>
        )}
        <h1 className={phone ? undefined : 'tnum'}>{phone ? (view === 'doc' ? '明细版' : '对账单详情') : `${d.statementNo}${view === 'doc' ? ' · 明细版' : ''}`}</h1>
        {!phone && <Tag tone={st.tone}>{st.label}</Tag>}
        <span className={s.sp} />
        {phone && <button type="button" className={s.moreButton} aria-label="更多操作" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}><Icon name="more" size={20} /></button>}
        {!phone && !embedded && <span className={s.hseg}>{seg}</span>}
        {!phone && !embedded && <span className={s.hpdf}>{pdf}</span>}
        {!phone && !embedded && status === 'DRAFT' && perms.statementWrite && <button type="button" className={s.headerSecondary} onClick={() => setRefreshOpen(true)}>重新取数</button>}
        {secondaryAction && !phone && !embedded && <button type="button" className={s.headerSecondary} onClick={() => setAction(secondaryAction)}>{label(secondaryAction)}</button>}
        {mainAction && !phone && !embedded && <button type="button" className={s.headerPrimary} onClick={() => setAction(mainAction)}>{label(mainAction)}</button>}
        {embedded && <Link className={s.open} to={`/settlements/statements/${d.id}${view === 'doc' ? '?v=doc' : ''}`}>全屏查看</Link>}
        {phone && moreOpen && <div className={s.moreMenu} role="menu" aria-label="对账单操作">
          {status === 'DRAFT' && perms.statementWrite && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setRefreshOpen(true); }}>重新取数</button>}
          {secondaryAction && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setAction(secondaryAction); }}>{label(secondaryAction)}</button>}
          {mainAction && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setAction(mainAction); }}>{label(mainAction)}</button>}
          {status !== 'CANCELLED' && perms.statementWrite && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setAction('cancel'); }}>作废对账单</button>}
        </div>}
      </header>
      {phone && view === 'doc' && <div className={s.docPhoneMeta}>{d.statementNo} · {st.label}</div>}
      {phone && view === 'doc' && <div className={s.segBar}>{seg}</div>}
      {embedded && <div className={s.tools}>{seg}<span className={s.sp} />{pdf}</div>}
      {supplierWorkspace ? (
        <div className={s.workspacePage}>
          <p className={s.workspaceSub}>给供应商的版本，按采购单分组列出每一项货物 <span>{d.businessDocuments.length} 张采购单 · {d.businessDocuments.reduce((n, doc) => n + (doc.goods?.length || 0), 0)} 个明细行</span></p>
          <SupplierStatementWorkspace d={d} />
        </div>
      ) : view === 'doc' ? (
        <StatementDoc d={d} />
      ) : phone ? (
        <div className={s.scroll}>{phoneMain}</div>
      ) : (
        <div className={s.scroll}>
          <p className={s.sub}>
            {d.partner.name} · {Number(y)} 年 {Number(m)} 月（{monthRange(d.period)}）· {displayedBasis}口径 · {d.createdBy?.name || '—'} {dateTime(d.createdAt)} 生成
          </p>
          <div className={s.grid}>
            <div className={s.col}>{main}</div>
            <aside className={s.col}>{info}</aside>
          </div>
        </div>
      )}
      {phone && view === 'doc' ? <footer className={s.bar}>
        <button type="button" className={s.barButton} disabled={sharing || status === 'CANCELLED'} onClick={() => void shareDocument()}>{sharing ? '准备中…' : '分享'}</button>
        <button type="button" className={s.primaryBarButton} onClick={() => setPdfOpen(true)}>导出 PDF</button>
      </footer> : phone && <footer className={s.bar}>
        {status === 'DRAFT' && perms.statementWrite && <>
          <button type="button" className={s.barButton} onClick={() => setRefreshOpen(true)}>重新取数</button>
          <button type="button" className={s.primaryBarButton} onClick={() => setAction('confirm')}>确认对账单</button>
        </>}
        {status === 'CONFIRMED' && <>
          {secondaryAction && <button type="button" className={s.barButton} onClick={() => setAction(secondaryAction)}>反确认</button>}
          {mainAction && <button type="button" className={s.primaryBarButton} onClick={() => setAction(mainAction)}>锁账</button>}
        </>}
        {status === 'LOCKED' && <>
          {mainAction && <button type="button" className={s.barButton} onClick={() => setAction(mainAction)}>解锁</button>}
          <button type="button" className={s.primaryBarButton} onClick={() => setPdfOpen(true)}>导出 PDF</button>
        </>}
        {status === 'CANCELLED' && <span className={s.barNote}>此对账单已作废</span>}
      </footer>}
      {actionDialog}
      {pdfOpen && <StatementPdfDialog data={d} canGenerate={perms.pdfWrite} onClose={() => setPdfOpen(false)} onGenerated={res.reload} />}
      {refreshOpen && <StatementRefreshDialog saved={d} onClose={() => setRefreshOpen(false)} onDone={() => { setRefreshOpen(false); res.reload(); history.reload(); }} />}
    </div>
  );
}
